// BUILD-046 task 8.8: the Assign task dialog model (design "Assign task dialog"; Req 3.2, 3.3, 3.10 - 3.13, 3.15, 3.18, 3.20).
//
// One dialog, one request contract (POST /work/assign), for every entry point. This module holds the dialog's state and rules; the
// component (AssignTaskDialog.tsx, task 8.15) renders it. Plain fields only: dictation is S2 and is NOT built here.
//
// RECORDED rule (Builder decision f041fa40, item 2, BC-2) for the assignment-mode choice:
//   shown   when the Recipient_Set has two or more Eligible_Employees, or the selection includes ANY Work_Group (even one with a single
//           current member);
//   hidden  for exactly one directly selected person with no group (that submission is a Direct_Assignment in their My_Tasks);
//   two options, "Anyone can complete" and "Everyone must complete", NEITHER preselected, and Submit stays disabled until one is chosen.
// The eligible count comes from the server preview (it de-duplicates and excludes), keyed to the exact selection it was computed for.
// Until a preview for the CURRENT selection is ready, the model decides from what is selected, and never hides a choice a group needs.
//
// Submission: a fresh submissionId is held for the life of the dialog and reused on every retry (the server answers a repeat with the
// same result, so a retry cannot create a second task); it changes only after a success, on close, or when the server says the id was
// already used with different entries (SUBMISSION_ID_REUSED, an ENGINEERING INTERPRETATION: the only way forward for an edited retry).
// While a submission is pending nothing more is sent; on failure every value the user entered is retained. NO_ELIGIBLE_RECIPIENTS is
// shown as "no eligible recipient" and is never shown as success.
//
// Pure module: no React, no I/O, no clock, no random (the caller supplies submission ids). Type imports, plus the pure priorityChoices module (the one list of
// priorities shared with the Edit form). Erasable TypeScript.

import type { AssignRequest } from '../../api/workShared';
import { DEFAULT_PRIORITY, PRIORITY_OPTIONS, isPriorityChoice } from './priorityChoices';
import type { PriorityChoice } from './priorityChoices';
import { isJobChoice } from './jobFieldModel';
import type { JobChoice } from './jobFieldModel';
import { isCustomerChoice } from './customerFieldModel';
import type { CustomerChoice } from './customerFieldModel';
// D1 display cleanup (display only: nothing stored or sent changes): a left-out person is written First name + last initial, without the "(FICTIONAL)" test marker.
import { personDisplay } from './displayNames';

export type AssignMode = 'anyone' | 'everyone';
export type AssignPhase = 'editing' | 'saving' | 'failed' | 'succeeded';
// The Next action is no longer a field of this dialog (Owner edit, October 20): it is set from Edit, not at creation, and POST /work/assign is no longer sent one.
export type AssignTextField = 'title' | 'details' | 'dueDate';

export const ASSIGN_MODE_OPTIONS: readonly { value: AssignMode; label: string }[] = Object.freeze([
  { value: 'anyone' as AssignMode, label: 'Anyone can complete' },
  { value: 'everyone' as AssignMode, label: 'Everyone must complete' },
]);

/**
 * The Priority choice of the dialog (Owner decisions 2026-10-20, OWNER-APPROVED - BUILDER RECONCILIATION REQUIRED): a live control with the SAME four choices as
 * the Edit form - Low, Medium, High and Urgent (priorityChoices.ts is the one list both use) - Medium by default, saved by POST /work/assign on every task the
 * request creates. Status is NOT a field of the dialog: the server writes every new task Active.
 */
export type AssignPriority = PriorityChoice;
export const DEFAULT_ASSIGN_PRIORITY: AssignPriority = DEFAULT_PRIORITY;
export const ASSIGN_PRIORITY_OPTIONS: readonly { value: AssignPriority; label: string }[] = PRIORITY_OPTIONS;

export interface SourceRefValue {
  module: string;
  recordId: string;
}

export interface PreviewData {
  eligibleCount: number;
  involvesGroup: boolean;
  excluded: { employeeId: string; name: string; reasons: string[] }[];
  queueEmptyAtIssue: boolean;
}

export interface PreviewSlot {
  /** The selection this preview was requested for; a preview for any other selection is ignored. */
  key: string;
  status: 'loading' | 'ready' | 'failed';
  data: PreviewData | null;
  message: string | null;
}

export type AssignFailureType =
  | 'no-eligible-recipients'
  | 'submission-reused'
  | 'mode-required'
  | 'validation'
  | 'not-authorized'
  | 'sign-in'
  | 'unavailable'
  // BUILD-046 J1a: the chosen Customer / Job was refused (unknown, archived or malformed), or the job list could not be checked when the request arrived.
  | 'job-not-available'
  | 'job-unavailable'
  // BUILD-046 customer association: the chosen Customer was refused (unknown, inactive or malformed), or the customer list could not be checked when the request arrived.
  | 'customer-not-available'
  | 'customer-unavailable'
  | 'other';

export interface AssignFailure {
  type: AssignFailureType;
  headline: string;
  detail: string | null;
  code: string | null;
  status: number;
}

export interface AssignResult {
  requestId: string;
  mode: 'direct' | AssignMode;
  replayed: boolean;
  createdCount: number;
  headline: string;
  /** One line per recipient who was left out, with every reason that applies (Req 3.8). */
  notices: string[];
  /** For an Everyone request, the "N of M" label. */
  aggregateLabel: string | null;
}

export interface AssignState {
  open: boolean;
  phase: AssignPhase;
  submissionId: string;
  title: string;
  details: string;
  dueDate: string;
  /** The chosen priority; Medium until the person picks another. It is kept on a failed submit and on a retry. */
  priority: AssignPriority;
  employeeIds: string[];
  groupIds: string[];
  collaborators: string[];
  mode: AssignMode | null;
  sourceRef: SourceRefValue | null;
  /**
   * The optional Customer / Job (BUILD-046 J1a): the stable id and the name shown in the chip. Only the id is sent; the server checks it against the job
   * directory and stores the name itself. It is separate from `sourceRef` and is NOT part of the recipient selection (so choosing a job never re-runs the
   * recipient preview). It is kept on a failed submit and on a retry.
   */
  job: JobChoice | null;
  /**
   * The optional Customer (BUILD-046 customer association), beside - and independent of - `job`: the stable id and the name shown in the chip. Only the id is sent;
   * the server checks it against the customer directory and stores the name itself. Choosing or clearing it never reads or writes `job`, and it is NOT part of the
   * recipient selection (so choosing a customer never re-runs the recipient preview). It is kept on a failed submit and on a retry.
   */
  customer: CustomerChoice | null;
  /** Set when the server said a mode is required that the client did not show. */
  serverRequiresMode: boolean;
  preview: PreviewSlot | null;
  /** Set by a refused submit; the missing-field message is then shown (and updates as the fields are fixed). */
  showMissing: boolean;
  failure: AssignFailure | null;
  result: AssignResult | null;
}

// ───────────────────────── open and close ─────────────────────────

export function closedAssignState(): AssignState {
  return {
    open: false,
    phase: 'editing',
    submissionId: '',
    title: '',
    details: '',
    dueDate: '',
    priority: DEFAULT_ASSIGN_PRIORITY,
    employeeIds: [],
    groupIds: [],
    collaborators: [],
    mode: null,
    sourceRef: null,
    job: null,
    customer: null,
    serverRequiresMode: false,
    preview: null,
    showMissing: false,
    failure: null,
    result: null,
  };
}

/**
 * Opens the dialog with NO person and NO group selected (Owner correction of October 19 15:49, which replaces the earlier "the Caller is preselected":
 * a task is never assigned to someone the Caller did not choose), and a fresh submission id for the life of the dialog (Req 3.14). Quick self-creation
 * is the explicit `assignToMe` action.
 */
export function openAssign(submissionId: string, sourceRef: SourceRefValue | null = null): AssignState {
  return { ...closedAssignState(), open: true, submissionId, employeeIds: [], sourceRef };
}

/** Closing discards everything: a reopened dialog starts clean with a new submission id. */
export function closeAssign(): AssignState {
  return closedAssignState();
}

// ───────────────────────── editing ─────────────────────────

function editable(state: AssignState): boolean {
  return state.open && (state.phase === 'editing' || state.phase === 'failed');
}

export function setField(state: AssignState, field: AssignTextField, value: string): AssignState {
  if (!editable(state) || typeof value !== 'string') return state;
  if (field !== 'title' && field !== 'details' && field !== 'dueDate') return state;
  return { ...state, [field]: value };
}

/** Chooses the priority. Only the four supported choices (Low, Medium, High, Urgent) are accepted; anything else leaves the state as it was. */
export function setPriority(state: AssignState, priority: string): AssignState {
  if (!editable(state) || !isPriorityChoice(priority)) return state;
  return state.priority === priority ? state : { ...state, priority };
}

/**
 * Chooses the Customer / Job (BUILD-046 J1a). Only a job with a usable id and name is accepted; anything else leaves the state as it was. Choosing again
 * replaces the earlier choice. The name held here is what the picker showed; the server stores the directory's own name.
 */
export function setJob(state: AssignState, job: unknown): AssignState {
  if (!editable(state) || !isJobChoice(job)) return state;
  if (state.job !== null && state.job.jobId === job.jobId && state.job.label === job.label) return state;
  return { ...state, job: { jobId: job.jobId, label: job.label.trim() } };
}

/**
 * Chooses the Customer (BUILD-046 customer association). Only a customer with a usable id and name is accepted; anything else leaves the state as it was.
 * Choosing again replaces the earlier choice. The name held here is what the picker showed; the server stores the directory's own name. Never touches `job`.
 */
export function setCustomer(state: AssignState, customer: unknown): AssignState {
  if (!editable(state) || !isCustomerChoice(customer)) return state;
  if (state.customer !== null && state.customer.customerId === customer.customerId && state.customer.label === customer.label) return state;
  return { ...state, customer: { customerId: customer.customerId, label: customer.label.trim() } };
}

/** Removes the chosen job before the task is created (the chip's Remove button). No job chosen changes nothing. */
export function clearJob(state: AssignState): AssignState {
  if (!editable(state) || state.job === null) return state;
  return { ...state, job: null };
}

/** Removes the chosen customer before the task is created (the chip's Remove button). No customer chosen changes nothing. Never touches `job`. */
export function clearCustomer(state: AssignState): AssignState {
  if (!editable(state) || state.customer === null) return state;
  return { ...state, customer: null };
}

function toggled(list: readonly string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export function toggleEmployee(state: AssignState, employeeId: string): AssignState {
  if (!editable(state) || typeof employeeId !== 'string' || employeeId === '') return state;
  return { ...state, employeeIds: toggled(state.employeeIds, employeeId) };
}

export function toggleGroup(state: AssignState, groupId: string): AssignState {
  if (!editable(state) || typeof groupId !== 'string' || groupId === '') return state;
  return { ...state, groupIds: toggled(state.groupIds, groupId) };
}

/**
 * The explicit "Assign to me" choice: the Caller is ADDED to the selected people, in one action, so a task for oneself stays quick. It is never done for
 * the Caller: the dialog opens empty. People and groups already chosen are left alone (nothing is removed silently), and doing it again changes nothing.
 */
export function assignToMe(state: AssignState, callerEmployeeId: string): AssignState {
  if (!editable(state) || typeof callerEmployeeId !== 'string' || callerEmployeeId === '') return state;
  if (state.employeeIds.includes(callerEmployeeId)) return state;
  return { ...state, employeeIds: [...state.employeeIds, callerEmployeeId] };
}

/** Adds one person (a chip). A person already selected is not added twice. */
export function addEmployee(state: AssignState, employeeId: string): AssignState {
  if (!editable(state) || typeof employeeId !== 'string' || employeeId === '' || state.employeeIds.includes(employeeId)) return state;
  return { ...state, employeeIds: [...state.employeeIds, employeeId] };
}

/** Removes one person (the chip's remove control). A person who is not selected changes nothing. */
export function removeEmployee(state: AssignState, employeeId: string): AssignState {
  if (!editable(state) || !state.employeeIds.includes(employeeId)) return state;
  return { ...state, employeeIds: state.employeeIds.filter((x) => x !== employeeId) };
}

// ───────────────────────── choosing people: search, chips, groups ─────────────────────────

export interface PersonOption {
  employeeId: string;
  displayName: string;
  department?: string;
}

/**
 * The people the search box offers: those whose display name contains what was typed (case-insensitive, ignoring surrounding spaces), in the order the
 * list came in, each person once. An empty query offers everyone (the open dropdown). People already chosen are STILL listed (the combobox marks them with
 * a check, so a Shift-click range stays contiguous); they are never added twice (addEmployee and the combobox's pick rule skip them).
 */
export function searchPeople<T extends PersonOption>(people: readonly T[], query: string): T[] {
  const needle = typeof query === 'string' ? query.trim().toLowerCase() : '';
  const taken = new Set<string>();
  const out: T[] = [];
  for (const person of people) {
    if (person === null || typeof person !== 'object' || typeof person.employeeId !== 'string' || person.employeeId === '') continue;
    if (taken.has(person.employeeId)) continue;
    const name = typeof person.displayName === 'string' ? person.displayName : '';
    if (needle !== '' && !name.toLowerCase().includes(needle)) continue;
    taken.add(person.employeeId);
    out.push(person);
  }
  return out;
}

export interface PersonChip {
  employeeId: string;
  displayName: string;
}

/** Chips for a list of ids (people or collaborators), in the order given, each once. A person the loaded list does not name reads "Unknown person". */
export function chipsFor(ids: readonly string[], people: readonly PersonOption[]): PersonChip[] {
  const byId = new Map<string, string>();
  for (const person of people) if (person && typeof person.employeeId === 'string' && typeof person.displayName === 'string' && person.displayName.trim() !== '') byId.set(person.employeeId, person.displayName.trim());
  return [...new Set(ids)].map((employeeId) => ({ employeeId, displayName: byId.get(employeeId) ?? 'Unknown person' }));
}

/** The selected people as removable chips, in the order they were chosen. A person the loaded list does not name reads "Unknown person". */
export function selectedPeople(state: AssignState, people: readonly PersonOption[]): PersonChip[] {
  return chipsFor(state.employeeIds, people);
}

export interface GroupOption {
  groupId: string;
  name: string;
  selected: boolean;
}

/** The work groups as a selectable list (the compact checkboxes), in the order given, with what is selected. */
export function groupOptions(groups: readonly { groupId: string; name: string }[], state: AssignState): GroupOption[] {
  return groups.filter((g) => g && typeof g.groupId === 'string' && g.groupId !== '').map((g) => ({ groupId: g.groupId, name: g.name, selected: state.groupIds.includes(g.groupId) }));
}

/** Collaborators are optional and never required (Req 3.12). */
export function toggleCollaborator(state: AssignState, employeeId: string): AssignState {
  if (!editable(state) || typeof employeeId !== 'string' || employeeId === '') return state;
  return { ...state, collaborators: toggled(state.collaborators, employeeId) };
}

export function setMode(state: AssignState, mode: AssignMode | null): AssignState {
  if (!editable(state)) return state;
  if (mode !== null && mode !== 'anyone' && mode !== 'everyone') return state;
  return { ...state, mode };
}

// ───────────────────────── preview ─────────────────────────

export function selectionKey(state: AssignState): string {
  return JSON.stringify([[...state.employeeIds].sort(), [...state.groupIds].sort(), state.sourceRef === null ? null : [state.sourceRef.module, state.sourceRef.recordId]]);
}

function hasRecipients(state: AssignState): boolean {
  return state.employeeIds.length + state.groupIds.length > 0;
}

/** The preview for the CURRENT selection, or null when none was requested or it is for an earlier selection. */
export function currentPreview(state: AssignState): PreviewSlot | null {
  return state.preview !== null && state.preview.key === selectionKey(state) ? state.preview : null;
}

/** The preview request to send for the current selection (the view debounces), or null when none is needed. */
export function previewRequest(state: AssignState): { key: string; request: AssignRequest } | null {
  if (!editable(state) || !hasRecipients(state)) return null;
  const key = selectionKey(state);
  if (state.preview !== null && state.preview.key === key) return null;
  const request: AssignRequest = { recipients: { employeeIds: unique(state.employeeIds), groupIds: unique(state.groupIds) } };
  if (state.sourceRef !== null) request.sourceRef = { ...state.sourceRef };
  return { key, request };
}

export function previewStarted(state: AssignState, key: string): AssignState {
  if (key !== selectionKey(state)) return state;
  return { ...state, preview: { key, status: 'loading', data: null, message: null } };
}

export function previewLoaded(state: AssignState, key: string, data: PreviewData): AssignState {
  if (key !== selectionKey(state) || data === null || typeof data !== 'object' || typeof data.eligibleCount !== 'number') return state;
  return {
    ...state,
    preview: {
      key,
      status: 'ready',
      message: null,
      data: {
        eligibleCount: data.eligibleCount,
        involvesGroup: data.involvesGroup === true,
        excluded: Array.isArray(data.excluded) ? data.excluded : [],
        queueEmptyAtIssue: data.queueEmptyAtIssue === true,
      },
    },
  };
}

/** A preview that failed blocks nothing: the dialog falls back to the selection and the server still decides on submit. */
export function previewFailed(state: AssignState, key: string, thrown: unknown): AssignState {
  if (key !== selectionKey(state)) return state;
  if (thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale') return state;
  const message = thrown !== null && typeof thrown === 'object' && typeof (thrown as { message?: unknown }).message === 'string' ? (thrown as { message: string }).message : null;
  return { ...state, preview: { key, status: 'failed', data: null, message } };
}

// ───────────────────────── the mode choice ─────────────────────────

/** Whether the mode choice is shown (the recorded rule in the header comment). */
export function modeChoiceVisible(state: AssignState): boolean {
  if (state.serverRequiresMode) return true;
  if (state.groupIds.length > 0) return true;
  const preview = currentPreview(state);
  if (preview !== null && preview.status === 'ready' && preview.data !== null) {
    return preview.data.involvesGroup || preview.data.eligibleCount >= 2;
  }
  return state.employeeIds.length >= 2;
}

// ───────────────────────── submit ─────────────────────────

export type MissingField = 'title' | 'recipients' | 'mode';

export function missingFields(state: AssignState): MissingField[] {
  const missing: MissingField[] = [];
  if (state.title.trim() === '') missing.push('title');
  if (!hasRecipients(state)) missing.push('recipients');
  if (modeChoiceVisible(state) && state.mode === null) missing.push('mode');
  return missing;
}

/** What each missing field says when it is shown beside its own field (the same words as `missingMessage`, one sentence each). */
export const PROBLEM_TEXT: Readonly<Record<MissingField, string>> = Object.freeze({
  title: 'Add a task title.',
  recipients: 'Choose at least one person or group.',
  mode: 'Choose Anyone can complete or Everyone must complete.',
});

export interface AssignProblem {
  field: MissingField;
  text: string;
}

/**
 * The problems to show NEXT TO their fields after a refused submit, in the order the fields appear (who first, then what): recipients, the mode choice,
 * then the title. They follow the entries live, so a problem disappears as soon as the field is fixed. `[]` until a submit was refused.
 */
export function assignProblems(state: AssignState): AssignProblem[] {
  if (!state.showMissing) return [];
  const missing = missingFields(state);
  return (['recipients', 'mode', 'title'] as MissingField[]).filter((field) => missing.includes(field)).map((field) => ({ field, text: PROBLEM_TEXT[field] }));
}

/** The message naming each missing field (Req 3.2). */
export function missingMessage(missing: readonly MissingField[]): string | null {
  if (missing.length === 0) return null;
  const parts: string[] = [];
  if (missing.includes('title')) parts.push('add a task title');
  if (missing.includes('recipients')) parts.push('choose at least one person or group');
  if (missing.includes('mode')) parts.push('choose Anyone can complete or Everyone must complete');
  const sentence = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

/**
 * The submit control is disabled while a submission is pending or finished, and until a mode is chosen whenever the choice is shown
 * (Req 3.11, 3.13). A missing title or recipient does NOT disable it: the submit is refused with a message naming the field (Req 3.2).
 */
export function submitDisabled(state: AssignState): boolean {
  if (!state.open || state.phase === 'saving' || state.phase === 'succeeded') return true;
  return modeChoiceVisible(state) && state.mode === null;
}

/** Each id once, in the order first given: a recipient is never sent twice. */
function unique(ids: readonly string[]): string[] {
  return [...new Set(ids)];
}

export function buildAssignRequest(state: AssignState): AssignRequest {
  const details = state.details.trim();
  // Sent: title, details, recipients, mode, priority (low, medium, high or urgent), dueDate (null when empty), collaborators, sourceRef, requiresEvidence.
  // NOT sent: nextAction (removed from this dialog) and status (the server writes every new task Active).
  // jobId (BUILD-046 J1a) is sent only when a job was chosen - the stable id, never the name - so a request without a job is exactly what it was before.
  // customerId (BUILD-046 J1a) is sent only when a customer was chosen - the stable id, never the name - so a request without a customer is exactly what it was before.
  return {
    submissionId: state.submissionId,
    title: state.title.trim(),
    details: details === '' ? null : details,
    recipients: { employeeIds: unique(state.employeeIds), groupIds: unique(state.groupIds) },
    mode: modeChoiceVisible(state) ? state.mode : null,
    priority: state.priority,
    dueDate: state.dueDate === '' ? null : state.dueDate,
    collaborators: unique(state.collaborators),
    sourceRef: state.sourceRef === null ? null : { ...state.sourceRef },
    requiresEvidence: false,
    ...(state.job !== null ? { jobId: state.job.jobId } : {}),
    ...(state.customer !== null ? { customerId: state.customer.customerId } : {}),
  };
}

/**
 * The submit handler. `request` is non-null exactly when the caller must now send it. A pending or finished submission sends nothing;
 * a submission with a missing title, recipient or mode is refused with a message naming each missing field and sends nothing.
 */
export function attemptSubmit(state: AssignState): { state: AssignState; request: AssignRequest | null } {
  if (!state.open || state.phase === 'saving' || state.phase === 'succeeded') return { state, request: null };
  if (missingFields(state).length > 0) return { state: state.showMissing ? state : { ...state, showMissing: true }, request: null };
  return { state: { ...state, phase: 'saving', failure: null, showMissing: false }, request: buildAssignRequest(state) };
}

const REASON_TEXT: Readonly<Record<string, string>> = Object.freeze({
  inactive: 'not active',
  not_authorized_for_source: 'not authorized for this source record',
});

/** One line per left-out recipient, naming the recipient and every reason that applies (Req 3.8). */
export function excludedNotices(excluded: readonly { name: string; reasons: readonly string[] }[] | undefined): string[] {
  if (!Array.isArray(excluded)) return [];
  return excluded.map((e) => {
    const given: readonly string[] = Array.isArray(e.reasons) ? e.reasons : [];
    const reasons = given.map((r) => REASON_TEXT[r] ?? String(r));
    const who = personDisplay(e.name).short;
    return `${who !== '' ? who : e.name} was left out: ${reasons.length > 0 ? reasons.join(' and ') : 'not eligible'}.`;
  });
}

interface AssignResponseLike {
  requestId?: unknown;
  mode?: unknown;
  replayed?: unknown;
  items?: unknown;
  excluded?: unknown;
  aggregate?: { label?: unknown } | null;
  queueEmptyAtIssue?: unknown;
}

/** The server accepted the submission. Applies only to a submission in progress; the submission id changes for any next one. */
export function submitSucceeded(state: AssignState, response: AssignResponseLike, nextSubmissionId: string): AssignState {
  if (state.phase !== 'saving' || response === null || typeof response !== 'object' || typeof response.requestId !== 'string') return state;
  const mode = response.mode === 'direct' || response.mode === 'anyone' || response.mode === 'everyone' ? response.mode : 'direct';
  const createdCount = Array.isArray(response.items) ? response.items.length : 0;
  const notices = excludedNotices(response.excluded as { name: string; reasons: string[] }[] | undefined);
  if (mode === 'anyone' && response.queueEmptyAtIssue === true) {
    notices.push('No one in the group can take this yet. It waits in the queue and oversight can see it as needing reassignment.');
  }
  const headline =
    mode === 'direct' ? 'Task assigned.' : mode === 'anyone' ? 'Shared task created. Anyone in the group can take it.' : `Task assigned to ${createdCount} ${createdCount === 1 ? 'person' : 'people'}.`;
  return {
    ...state,
    phase: 'succeeded',
    submissionId: nextSubmissionId,
    failure: null,
    showMissing: false,
    result: {
      requestId: response.requestId,
      mode,
      replayed: response.replayed === true,
      createdCount,
      headline,
      notices,
      aggregateLabel: response.aggregate && typeof response.aggregate.label === 'string' ? response.aggregate.label : null,
    },
  };
}

interface ThrownLike {
  kind?: unknown;
  status?: unknown;
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

/**
 * The server's reason for a refused job (`details.reason`), or null. The codes are shared with other refusals; the reason tells them apart. `lookup-unavailable` is
 * shared by the job and the customer directory, so it is read as a JOB reason unless the server names the customer field (`details.field === 'customerId'`).
 */
function jobReasonOf(t: ThrownLike): 'job-not-selectable' | 'lookup-unavailable' | null {
  const details = t.details;
  if (details === null || typeof details !== 'object') return null;
  const reason = (details as { reason?: unknown }).reason;
  if (reason === 'lookup-unavailable' && (details as { field?: unknown }).field === 'customerId') return null;
  return reason === 'job-not-selectable' || reason === 'lookup-unavailable' ? reason : null;
}
/**
 * The server's reason for a refused customer (`details.reason`), or null. `customer-not-selectable` is specific to customers; `lookup-unavailable` is a customer reason
 * only when the server names the customer field (`details.field === 'customerId'`), so a job directory outage is never worded as a customer one.
 */
function customerReasonOf(t: ThrownLike): 'customer-not-selectable' | 'lookup-unavailable' | null {
  const details = t.details;
  if (details === null || typeof details !== 'object') return null;
  const reason = (details as { reason?: unknown }).reason;
  if (reason === 'customer-not-selectable') return reason;
  return reason === 'lookup-unavailable' && (details as { field?: unknown }).field === 'customerId' ? reason : null;
}

export const NOT_ASSIGNED = 'The task was not assigned.';

export function describeAssignFailure(thrown: unknown): AssignFailure {
  const t: ThrownLike = thrown !== null && typeof thrown === 'object' ? (thrown as ThrownLike) : {};
  const status = typeof t.status === 'number' ? t.status : 0;
  const code = typeof t.code === 'string' ? t.code : null;
  const serverMessage = typeof t.message === 'string' && t.message.trim() !== '' ? t.message : null;
  const make = (type: AssignFailureType, headline: string, detail: string | null): AssignFailure => ({ type, headline, detail, code, status });

  // The Customer / Job was refused (BUILD-046 J1a). Nothing was created, and every entry (the chosen job included) is kept so it can be changed and sent again.
  const jobReason = jobReasonOf(t);
  if (jobReason === 'job-not-selectable' && status === 400) {
    return make('job-not-available', NOT_ASSIGNED, 'That job is not available. Choose another job or remove the job, then assign again. Your other entries are kept.');
  }
  if (jobReason === 'lookup-unavailable' && status >= 500) {
    return make('job-unavailable', NOT_ASSIGNED, "The job list can't be checked right now, so nothing was created. Your entries are kept. Try again, or remove the job and assign without it.");
  }
  // The Customer was refused (BUILD-046 J1a). Nothing was created, and every entry (the chosen customer included) is kept so it can be changed and sent again.
  const customerReason = customerReasonOf(t);
  if (customerReason === 'customer-not-selectable' && status === 400) {
    return make('customer-not-available', NOT_ASSIGNED, 'That customer is not available. Choose another customer or remove the customer, then assign again. Your other entries are kept.');
  }
  if (customerReason === 'lookup-unavailable' && status >= 500) {
    return make('customer-unavailable', NOT_ASSIGNED, "The customer list can't be checked right now, so nothing was created. Your entries are kept. Try again, or remove the customer and assign without it.");
  }
  if (code === 'NO_ELIGIBLE_RECIPIENTS') {
    return make('no-eligible-recipients', 'There is no eligible recipient.', 'Nothing was created. Change who it is for and try again.');
  }
  if (code === 'MODE_REQUIRED') {
    return make('mode-required', 'Choose how this should be completed.', 'Pick Anyone can complete or Everyone must complete, then submit again.');
  }
  if (code === 'SUBMISSION_ID_REUSED') {
    return make('submission-reused', NOT_ASSIGNED, 'This submission was already used with different entries. Submit again to send it as a new request.');
  }
  if (t.kind === 'timeout' || t.kind === 'network') {
    return make('unavailable', NOT_ASSIGNED, 'We could not confirm it. Your entries are kept. Submitting again is safe: the same request cannot create a second task.');
  }
  if (status === 401) return make('sign-in', NOT_ASSIGNED, serverMessage ?? 'Your identity could not be verified.');
  if (status === 403) return make('not-authorized', NOT_ASSIGNED, serverMessage ?? 'You are not allowed to assign this.');
  if (status >= 500) return make('unavailable', NOT_ASSIGNED, serverMessage ?? 'Something went wrong. Nothing was changed.');
  if (status >= 400) return make('validation', NOT_ASSIGNED, serverMessage);
  return make('other', NOT_ASSIGNED, serverMessage);
}

/**
 * The submission failed. Every entered value stays exactly as it was (Req 3.18). A stale answer (the Caller changed) is ignored: the host
 * closes the dialog on a Caller change. `nextSubmissionId` is used only when the server rejected the id as already used.
 */
export function submitFailed(state: AssignState, thrown: unknown, nextSubmissionId: string): AssignState {
  if (state.phase !== 'saving') return state;
  if (thrown !== null && typeof thrown === 'object' && (thrown as ThrownLike).kind === 'stale') return state;
  const failure = describeAssignFailure(thrown);
  return {
    ...state,
    phase: 'failed',
    failure,
    result: null,
    showMissing: false,
    serverRequiresMode: state.serverRequiresMode || failure.type === 'mode-required',
    submissionId: failure.type === 'submission-reused' ? nextSubmissionId : state.submissionId,
  };
}

// ───────────────────────── what the dialog shows ─────────────────────────

export interface AssignView {
  open: boolean;
  /** What is selected: people and groups, each counted once. `empty` is true when neither is (submit then says what is missing). */
  recipients: { peopleCount: number; groupCount: number; empty: boolean };
  /** The explicit "Assign to me" choice: available once the Caller is known, `selected` when they are among the people chosen. */
  assignToMe: { available: boolean; selected: boolean };
  /** Inputs are disabled while saving and after success. */
  inputsDisabled: boolean;
  modeChoice: { visible: boolean; options: { value: AssignMode; label: string; checked: boolean }[] };
  /** The Priority control: the chosen value and its options (Low, Medium, High, Urgent). */
  priority: { value: AssignPriority; options: { value: AssignPriority; label: string; selected: boolean }[] };
  submit: { disabled: boolean; label: 'Assign task' | 'Saving…'; showSaving: boolean };
  /** The message naming each missing field, after a refused submit. */
  missingMessage: string | null;
  /** The same problems one per field, in the order the fields appear, for display beside each field; the first is where focus goes. */
  problems: AssignProblem[];
  failure: AssignFailure | null;
  /** Set only after the server accepted the submission. Never set together with `failure`. */
  result: AssignResult | null;
  preview: { status: 'none' | 'loading' | 'ready' | 'failed'; eligibleCount: number | null; excludedNotices: string[]; queueEmptyAtIssue: boolean; message: string | null };
}

export function describeAssign(state: AssignState, context: { callerEmployeeId?: string | null } = {}): AssignView {
  const preview = currentPreview(state);
  const visible = modeChoiceVisible(state);
  const saving = state.phase === 'saving';
  const caller = typeof context.callerEmployeeId === 'string' ? context.callerEmployeeId : '';
  return {
    open: state.open,
    recipients: { peopleCount: unique(state.employeeIds).length, groupCount: unique(state.groupIds).length, empty: !hasRecipients(state) },
    assignToMe: { available: caller !== '', selected: caller !== '' && state.employeeIds.includes(caller) },
    inputsDisabled: saving || state.phase === 'succeeded',
    modeChoice: {
      visible,
      options: ASSIGN_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label, checked: state.mode === o.value })),
    },
    priority: { value: state.priority, options: ASSIGN_PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label, selected: state.priority === o.value })) },
    submit: { disabled: submitDisabled(state), label: saving ? 'Saving…' : 'Assign task', showSaving: saving },
    missingMessage: state.showMissing ? missingMessage(missingFields(state)) : null,
    problems: assignProblems(state),
    failure: state.phase === 'failed' ? state.failure : null,
    result: state.phase === 'succeeded' ? state.result : null,
    preview: {
      status: preview === null ? 'none' : preview.status,
      eligibleCount: preview !== null && preview.data !== null ? preview.data.eligibleCount : null,
      excludedNotices: preview !== null && preview.data !== null ? excludedNotices(preview.data.excluded) : [],
      queueEmptyAtIssue: preview !== null && preview.data !== null ? preview.data.queueEmptyAtIssue : false,
      message: preview !== null ? preview.message : null,
    },
  };
}
